import { useRef, useState } from 'react'
import { MessageIcon, MessageIconlarge } from '../atoms/Icons/MessageIcon'
import WarningIcon from '../atoms/Icons/WarningIcon.svg'
import ErrorModal from './ErrorModal'
import OptionsModal from './OptionsModal'

const EMAIL = 'mailto:grapes@theagilemonkeys.com'

interface SuggestionsBoxProps {
  textClassName?: string
  iconColor?: string
}

export const SuggestionsBox = ({ textClassName = 'text-orange', iconColor = '#EC6823' }: SuggestionsBoxProps) => {
  const [showSuggestionsModal, setShowSuggestionsModal] = useState<boolean>(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState<boolean>(false)
  const [showEmptyMessageModal, setShowEmptyMessageModal] = useState(false)
  const [lastMessage, setLastMessage] = useState<string>('')
  const suggestionRef = useRef<HTMLTextAreaElement | null>(null)

  const SuggestionsBoxTemplate = () => {
    return (
      <div className="w-[87%] mx-auto">
        <p className="text-[1.8rem] leading-[2.6rem] mb-4">
          If you have any suggestions, questions, or have experienced any issues with Grapes, we’d love to know. Your
          feedback is gold to us!
        </p>
        <h3 className="text-left text-2xl font-bold leading-6 mb-4">Message</h3>
        <div className="app-textarea-wrapper">
          <textarea
            ref={suggestionRef}
            className="app-textarea-textarea app-textarea-textarea--suggestion resize-none"
            placeholder="Write your message here and we’ll get back to you ASAP"
          >
            {lastMessage}
          </textarea>
        </div>
      </div>
    )
  }

  // An uncontrolled textarea was needed because, when inside a modal, Primereact InpuTextarea was losing focus every time the input changes.
  const handleSend = () => {
    setShowSuggestionsModal(false)
    const message = suggestionRef?.current?.value
    setLastMessage(message ?? '')
    if (!message?.length) {
      setShowEmptyMessageModal(true)
    } else {
      const suggestionEmailBody = `Hi Grapes team, I’d like to share this suggestion: ${message}. Thanks for reviewing it.`
      window.open(
        `${EMAIL}?subject=${encodeURIComponent('Grapes suggestion')}&body=${encodeURIComponent(suggestionEmailBody)}`,
        '_blank'
      )
      setLastMessage('')
    }
  }

  const closeSuggestionBox = () => {
    const message = suggestionRef?.current?.value || ''
    setLastMessage(message)
    if (message) {
      setShowSuggestionsModal(false)
      setShowCloseConfirmation(true)
    } else {
      setShowSuggestionsModal(false)
      setShowCloseConfirmation(false)
    }
  }

  const returnToSuggestionBox = () => {
    setShowCloseConfirmation(false)
    setShowSuggestionsModal(true)
  }

  const closeConfirmationAndSuggestion = () => {
    setShowCloseConfirmation(false)
    setShowSuggestionsModal(false)
    setLastMessage('')
  }

  return (
    <>
      <div className="flex-1 flex justify-end items-center">
        <div
          className={`flex items-center gap-1 cursor-pointer text-xl font-bold hover:underline ${textClassName}`}
          onClick={() => setShowSuggestionsModal(!showSuggestionsModal)}
        >
          <MessageIcon color={iconColor} />
          Suggestions
        </div>
      </div>
      {showCloseConfirmation && (
        <OptionsModal
          icon={<WarningIcon width={67} height={72}></WarningIcon>}
          visible={showCloseConfirmation}
          message={
            <div>
              <p className="text-[3.4rem] font-normal w-[50%] mb-8 mx-auto">Are you sure you want to close?</p>
              <p className="text-[1.8rem] font-bold">Your message has not been sent and will be lost.</p>
            </div>
          }
          firstButtonText="No, go back."
          firstButtonType="clear"
          secondButtonText="Yes"
          secondButtonType="info"
          onFirstButtonClick={returnToSuggestionBox}
          onSecondButtonClick={closeConfirmationAndSuggestion}
          onClose={returnToSuggestionBox}
        />
      )}
      {showSuggestionsModal && (
        <OptionsModal
          icon={<MessageIconlarge />}
          visible={showSuggestionsModal}
          message={<SuggestionsBoxTemplate />}
          firstButtonText="Close"
          firstButtonType="clear"
          secondButtonText="Send"
          secondButtonType="info"
          onFirstButtonClick={closeSuggestionBox}
          onSecondButtonClick={handleSend}
          onClose={closeSuggestionBox}
        />
      )}
      {showEmptyMessageModal && (
        <ErrorModal
          icon={<WarningIcon />}
          visible={showEmptyMessageModal}
          message={
            <p>
              <span className={`modal-title`}>Your suggestion message is empty</span>
              <br />
              <span className="text-grey">
                <b>Please submit a non-empty message.</b>
              </span>
            </p>
          }
          onClose={() => {
            setShowEmptyMessageModal(false)
            setShowSuggestionsModal(true)
          }}
        />
      )}
    </>
  )
}
